import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Crown,
  ShieldCheck,
  User,
  UserX,
  Mail,
  Plus,
  MoreHorizontal,
  Check,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import api from "../api/api";
import { useSelector } from "react-redux";
import { useConfirm } from "../contexts/ConfirmContext";
import { useEntitlementBlockerModal } from "../contexts/EntitlementBlockerModalContext";

const ROLE_ICONS = {
  OWNER: Crown,
  ADMIN: ShieldCheck,
  MEMBER: User,
};

const ROLE_COLORS = {
  OWNER: "text-amber-500",
  ADMIN: "text-blue-500",
  MEMBER: "text-muted-foreground",
};

function RoleBadge({ role }) {
  const Icon = ROLE_ICONS[role] || User;
  return (
    <Badge
      variant="outline"
      className="gap-1.5 font-normal text-[11px] px-2 py-0.5"
    >
      <Icon className={`h-3 w-3 ${ROLE_COLORS[role]}`} />
      {role}
    </Badge>
  );
}

export default function TeamSettings() {
  const { activeWorkspace } = useSelector((state) => state.workspace);
  const { showEntitlementBlocker } = useEntitlementBlockerModal();
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("MEMBER");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get("/api/workspace/members"),
        api.get("/api/workspace/invites").catch(() => ({ data: [] })), // may fail if MEMBER
      ]);
      setMembers(membersRes.data);
      setPendingInvites(invitesRes.data);
    } catch (err) {
      console.error("Failed to fetch team:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeWorkspace?._id) {
      fetchMembers();
    }
  }, [activeWorkspace, fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const { data } = await api.post("/api/workspace/invites", {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteResult({
        type: "success",
        message: data.message,
        token: data.inviteToken,
      });
      setInviteEmail("");
      fetchMembers();
    } catch (err) {
      const entitlementHandled = showEntitlementBlocker(err, {
        context: { surface: "team-settings", action: "member-invite" },
      });
      const msg = entitlementHandled
        ? err?.response?.data?.message ||
          "Invite blocked by your current plan entitlements."
        : err?.response?.data?.message ||
          err?.message ||
          "Failed to send invite.";
      setInviteResult({ type: "error", message: msg });
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/workspace/members/${userId}/role`, { role: newRole });
      fetchMembers();
    } catch (err) {
      console.error("Role change failed:", err?.response?.data?.message);
    }
  };

  const confirm = useConfirm();

  const handleRemove = async (userId) => {
    const ok = await confirm(
      "Remove Member?",
      "Remove this member from the workspace? This action cannot be undone.",
    );
    if (!ok) return;
    try {
      await api.delete(`/api/workspace/members/${userId}`);
      fetchMembers();
    } catch (err) {
      console.error("Remove failed:", err?.response?.data?.message);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Team Management
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage members and invitations for{" "}
            <span className="text-foreground font-medium">
              {activeWorkspace?.name || "your workspace"}
            </span>
            .
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setInviteOpen(true);
            setInviteResult(null);
          }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Invite Member
        </Button>
      </div>

      {/* Active Members Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="text-xs font-medium tracking-wide uppercase px-4">
                Member
              </TableHead>
              <TableHead className="text-xs font-medium tracking-wide uppercase">
                Role
              </TableHead>
              <TableHead className="text-xs font-medium tracking-wide uppercase hidden md:table-cell">
                Joined
              </TableHead>
              <TableHead className="text-xs font-medium tracking-wide uppercase hidden lg:table-cell">
                Last Active
              </TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground text-sm"
                >
                  Loading members...
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground text-sm"
                >
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No members found.
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow key={m.id} className="hover:bg-muted/20">
                  <TableCell className="px-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                        {(m.name || m.email || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{m.name || "—"}</p>
                        <p className="text-xs text-muted-foreground">
                          {m.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={m.role} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                    {m.joinedAt
                      ? new Date(m.joinedAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                    {m.lastLoginAt
                      ? new Date(m.lastLoginAt).toLocaleDateString()
                      : "Never"}
                  </TableCell>
                  <TableCell>
                    {m.role !== "OWNER" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-40 bg-popover border border-border shadow-md rounded-xl p-1"
                        >
                          <DropdownMenuItem
                            className="text-xs"
                            onClick={() => handleRoleChange(m.userId, "ADMIN")}
                          >
                            Make Admin
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-xs"
                            onClick={() => handleRoleChange(m.userId, "MEMBER")}
                          >
                            Make Member
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-xs text-destructive focus:text-destructive focus:bg-destructive/10"
                            onClick={() => handleRemove(m.userId)}
                          >
                            <UserX className="h-3.5 w-3.5 mr-1.5" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" /> Pending
            Invitations
          </h3>
          <div className="space-y-2">
            {pendingInvites.map((inv) => (
              <div
                key={inv._id}
                className="flex items-center justify-between rounded-lg border border-border px-4 py-2.5 bg-muted/10"
              >
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm">{inv.email}</span>
                  <RoleBadge role={inv.role} />
                </div>
                <span className="text-xs text-muted-foreground">
                  Sent {new Date(inv.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent side="right" className="sm:max-w-md bg-background border border-border">
          <SheetHeader>
            <SheetTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4" /> Invite Member
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Email address
              </label>
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteResult(null);
                }}
                className="bg-muted/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-muted/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="OWNER">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteResult && (
              <div
                className={`rounded-lg px-3 py-2.5 text-sm flex items-start gap-2 ${inviteResult.type === "success" ? "bg-green-500/10 text-green-600 border border-green-500/20" : "bg-destructive/10 text-destructive border border-destructive/20"}`}
              >
                {inviteResult.type === "success" ? (
                  <Check className="h-4 w-4 mt-0.5 shrink-0" />
                ) : (
                  <X className="h-4 w-4 mt-0.5 shrink-0" />
                )}
                <div>
                  <p>{inviteResult.message}</p>
                  {inviteResult.token && (
                    <p className="text-xs mt-1 opacity-70 font-mono">
                      Token: {inviteResult.token}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <SheetFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setInviteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail}
            >
              {inviting ? "Sending…" : "Send Invite"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
